import { panic } from '@pkgs/api/utils/panic';
import { type HttpRequestConfig } from '@pkgs/api/types/http';
import { type ApiConfig } from '@pkgs/api/types/api';

import { getUserAgent, isBrowserRuntime } from './userAgent';
import { mergeHeaders } from './requestConfig';
import { HttpError, isHttpError } from './httpError';

export type CustomFetchClientOptions = {
  defaultBaseURL: string;
  defaultMaxRetryCount?: number;
  defaultRetryDelayMs?: number;
  defaultRetryStatusCodes?: number[];
};

type RequestSignalState = {
  cleanup: () => void;
  didTimeout: () => boolean;
  signal?: AbortSignal;
};

const wait = (durationMs: number) => {
  if (durationMs <= 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    setTimeout(resolve, durationMs);
  });
};

export class CustomFetchClient {
  constructor(private readonly options: CustomFetchClientOptions) {}

  async request<T>(requestConfig: HttpRequestConfig, apiConfig?: ApiConfig): Promise<T> {
    const timeout = apiConfig?.timeout;
    const maxRetryCount = apiConfig?.maxRetryCount ?? this.options.defaultMaxRetryCount ?? 0;
    const retryDelayMs = apiConfig?.retryDelayMs ?? this.options.defaultRetryDelayMs ?? 250;
    const retryStatusCodes = new Set(apiConfig?.retryStatusCodes ?? this.options.defaultRetryStatusCodes ?? [429]);

    const headers = await this.getHeaders(requestConfig, apiConfig);

    if (!headers['x-application-key']) {
      panic('x-application-key is required');
    }

    const url = this.getUrl(requestConfig, apiConfig);
    const resolvedRequestConfig: HttpRequestConfig = {
      ...requestConfig,
      headers,
      url,
    };

    for (let attempt = 0; attempt <= maxRetryCount; attempt += 1) {
      const signalState = this.createRequestSignal(apiConfig?.signal ?? requestConfig.signal, timeout);

      try {
        const response = await fetch(url, {
          body: requestConfig.data === undefined ? undefined : JSON.stringify(requestConfig.data),
          headers,
          method: requestConfig.method,
          signal: signalState.signal,
        });
        const data = (await response.json().catch(() => undefined)) as T | undefined;

        if (!response.ok) {
          throw HttpError.fromResponse(response, {
            data,
            request: resolvedRequestConfig,
          });
        }

        return data as T;
      } catch (error) {
        const normalizedError = this.getHttpError(error, {
          didTimeout: signalState.didTimeout(),
          requestConfig: resolvedRequestConfig,
          timeout,
        });

        if (attempt >= maxRetryCount || !this.shouldRetry(normalizedError, retryStatusCodes)) {
          throw normalizedError;
        }

        await wait(retryDelayMs * 2 ** attempt);
      } finally {
        signalState.cleanup();
      }
    }

    throw new HttpError('The request failed before a response was returned.', {
      code: 'ERR_NETWORK',
      request: resolvedRequestConfig,
    });
  }

  private createRequestSignal(signal: AbortSignal | undefined, timeout: number | undefined): RequestSignalState {
    if (!signal && !timeout) {
      return {
        cleanup: () => {},
        didTimeout: () => false,
        signal: undefined,
      };
    }

    const controller = new AbortController();
    let didTimeout = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const abortFromSignal = () => {
      controller.abort(signal?.reason);
    };

    if (signal) {
      if (signal.aborted) {
        controller.abort(signal.reason);
      } else {
        signal.addEventListener('abort', abortFromSignal, { once: true });
      }
    }

    if (timeout && timeout > 0) {
      timeoutId = setTimeout(() => {
        didTimeout = true;
        controller.abort();
      }, timeout);
    }

    return {
      cleanup: () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        if (signal) {
          signal.removeEventListener('abort', abortFromSignal);
        }
      },
      didTimeout: () => didTimeout,
      signal: controller.signal,
    };
  }

  private async getHeaders(requestConfig: HttpRequestConfig, apiConfig?: ApiConfig) {
    const jwtToken = apiConfig?.getToken ? await apiConfig.getToken({ requestConfig }) : apiConfig?.jwtToken;
    const baseHeaders: Record<string, string> = {};

    if (apiConfig?.applicationKey) {
      baseHeaders['x-application-key'] = apiConfig.applicationKey;

      if (!isBrowserRuntime()) {
        baseHeaders['user-agent'] = getUserAgent(apiConfig.applicationKey);
      }
    }

    if (apiConfig?.privateOfficePassword) {
      baseHeaders['X-PrivateOfficePassword'] = apiConfig.privateOfficePassword;
    }

    if (apiConfig?.basicAuth) {
      baseHeaders['Authorization'] = `Basic ${btoa(`${apiConfig.basicAuth.username}:${apiConfig.basicAuth.password}`)}`;
    }

    if (jwtToken) {
      baseHeaders['Authorization'] = `Bearer ${jwtToken}`;
    }

    return mergeHeaders(baseHeaders, apiConfig?.headers, requestConfig.headers);
  }

  private getUrl(requestConfig: HttpRequestConfig, apiConfig?: ApiConfig) {
    const url = new URL(apiConfig?.baseURL ?? this.options.defaultBaseURL);
    const basePathname = url.pathname === '/' ? '' : url.pathname.replace(/\/$/, '');
    const requestPathname = requestConfig.url.startsWith('/') ? requestConfig.url : `/${requestConfig.url}`;
    const searchParams = new URLSearchParams();

    for (const [key, value] of Object.entries(requestConfig.params ?? {})) {
      if (value == null) {
        continue;
      }

      if (Array.isArray(value)) {
        for (const item of value) {
          if (item != null) {
            searchParams.append(key, String(item));
          }
        }

        continue;
      }

      searchParams.set(key, String(value));
    }

    url.pathname = `${basePathname}${requestPathname}`;
    url.search = searchParams.toString();

    return url.toString();
  }

  private getHttpError(
    error: unknown,
    {
      didTimeout,
      requestConfig,
      timeout,
    }: {
      didTimeout: boolean;
      requestConfig: HttpRequestConfig;
      timeout: number | undefined;
    },
  ) {
    if (isHttpError(error)) {
      return error;
    }

    if (didTimeout) {
      return new HttpError(`timeout of ${timeout}ms exceeded`, {
        cause: error,
        code: 'ETIMEDOUT',
        request: requestConfig,
      });
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      return new HttpError('Request canceled', {
        cause: error,
        code: 'ERR_CANCELED',
        request: requestConfig,
      });
    }

    if (error instanceof Error) {
      return new HttpError(error.message, {
        cause: error,
        code: 'ERR_NETWORK',
        request: requestConfig,
      });
    }

    return new HttpError('An unknown network error occurred.', {
      cause: error,
      code: 'ERR_NETWORK',
      request: requestConfig,
    });
  }

  private shouldRetry(error: HttpError, retryStatusCodes: Set<number>) {
    return Boolean(error.response && retryStatusCodes.has(error.response.status));
  }
}
